import { useEffect, useRef, useState } from 'react';

import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { dataroomApi } from '../api/dataroomApi';
import { useCreateFirstLabelRunMutation, useUpdateFirstLabelReportMutation } from '../api/firstLabelApi';
import { FIRST_LABEL_STAGES, stageIndexForStatus, useFirstLabelRun } from '../firstLabelRun';
import { ConfirmModal } from './ConfirmModal';
import { ReportUpdateSummary } from './ReportUpdateSummary';

/**
 * First Label indicator for the files panel.
 *
 * The analysis starts automatically with "Evaluate company". A failed run can
 * be retried; it researches the company from scratch and replaces the
 * deliverables, so it asks for confirmation first.
 *
 * A finished report is not researched again. When the user adds their own
 * documents to the dataroom, it offers to update the report from them: the
 * report is revised from those documents only and rendered again. With no new
 * document there is nothing to offer, so no button shows. After an update,
 * "Changes" opens what it changed, since grades move with no approval step.
 *
 * readOnly (guests): outcome is still shown, but rerun/retry are writes and
 * disappear entirely, confirmation modal included.
 */
export function FirstLabelStatus({ projectUuid, readOnly = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    run, isActive, hasDeliverables, isUpdating, pendingDocuments, latestUpdate,
  } = useFirstLabelRun(projectUuid);
  const [createFirstLabelRun, { isLoading: isStarting }] = useCreateFirstLabelRunMutation();
  const [updateFirstLabelReport, { isLoading: isStartingUpdate }] = useUpdateFirstLabelReportMutation();
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isUpdateConfirmOpen, setUpdateConfirmOpen] = useState(false);
  const [isSummaryOpen, setSummaryOpen] = useState(false);

  // When the active run or update finishes, refresh the dataroom so the new
  // deliverables, or the new version of the report, appear.
  const isWorking = Boolean(isActive || isUpdating);
  const wasWorking = useRef(false);
  useEffect(() => {
    if (wasWorking.current && run && !isWorking) {
      dispatch(dataroomApi.util.invalidateTags([
        { type: 'Folder', id: 'LIST' },
        { type: 'Document', id: 'LIST' },
      ]));
    }
    wasWorking.current = isWorking;
  }, [isWorking, run, dispatch]);

  if (!projectUuid || !run) return null;

  const confirmRerun = () => {
    setConfirmOpen(false);
    createFirstLabelRun({ projectUuid, company: run.company_name }).catch(() => {});
  };

  const confirmUpdate = () => {
    setUpdateConfirmOpen(false);
    updateFirstLabelReport({ runUuid: run.uuid }).catch(() => {});
  };

  const rerunModal = (
    <ConfirmModal
      isOpen={isConfirmOpen}
      onConfirm={confirmRerun}
      onCancel={() => setConfirmOpen(false)}
      title={t('dataroom.firstLabel.status.rerunTitle')}
      message={
        hasDeliverables
          ? t('dataroom.firstLabel.status.rerunMessageWithDeliverables')
          : t('dataroom.firstLabel.status.rerunMessage')
      }
      confirmLabel={isStarting ? t('dataroom.firstLabel.status.starting') : t('dataroom.firstLabel.status.runAgain')}
      variant="warning"
    />
  );

  if (isActive) {
    // Keep the sidebar label short; the full-screen gate carries the detail.
    const stage = FIRST_LABEL_STAGES[stageIndexForStatus(run.status)];
    return (
      <span
        className="flex items-center gap-1 text-xs text-[var(--color-accent)] whitespace-nowrap"
        title={t('dataroom.firstLabel.status.activeTitle', { stage: t(stage.label) })}
        data-testid="first-label-status"
      >
        <span className="animate-spin inline-block size-3 border-[2px] border-current border-t-transparent rounded-full" />
        {t('dataroom.firstLabel.status.generating')}
      </span>
    );
  }

  // Quality scores are internal (admin/ops); the UI only shows outcome.
  if (run.status === 'ready') {
    if (isUpdating) {
      return (
        <span
          className="flex items-center gap-1 text-xs text-[var(--color-accent)] whitespace-nowrap"
          title={t('dataroom.firstLabel.status.updatingTitle')}
          data-testid="first-label-updating"
        >
          <span className="animate-spin inline-block size-3 border-[2px] border-current border-t-transparent rounded-full" />
          {t('dataroom.firstLabel.status.updating')}
        </span>
      );
    }
    const updateFailed = latestUpdate?.status === 'failed';
    const canUpdate = !readOnly && pendingDocuments > 0;
    const revision = latestUpdate?.status === 'ready' && latestUpdate.revision?.summary
      ? latestUpdate.revision
      : null;
    return (
      <>
        <span
          className={`flex items-center gap-1 text-xs whitespace-nowrap ${updateFailed ? 'text-red-500' : 'text-emerald-500'}`}
          data-testid="first-label-ready"
        >
          {updateFailed ? (
            <Icon
              icon="mdi:alert-circle-outline"
              className="size-3.5"
              title={t('dataroom.firstLabel.status.updateFailed')}
              data-testid="first-label-update-failed"
            />
          ) : (
            <Icon icon="mdi:check-circle" className="size-3.5" title={t('dataroom.firstLabel.status.readyTitle')} />
          )}
          {canUpdate && (
            <button
              type="button"
              onClick={() => setUpdateConfirmOpen(true)}
              disabled={isStartingUpdate}
              data-testid="first-label-update"
              title={t('dataroom.firstLabel.status.updateHint')}
              className="cursor-pointer text-[var(--color-text-secondary)] underline decoration-dotted hover:text-[var(--color-text)] disabled:opacity-50"
            >
              {isStartingUpdate
                ? t('dataroom.firstLabel.status.starting')
                : t('dataroom.firstLabel.status.updateWithDocuments', { count: pendingDocuments })}
            </button>
          )}
          {revision && !canUpdate && (
            <button
              type="button"
              onClick={() => setSummaryOpen(true)}
              data-testid="first-label-changes"
              title={t('dataroom.firstLabel.status.changesHint')}
              className="cursor-pointer text-[var(--color-text-secondary)] underline decoration-dotted hover:text-[var(--color-text)]"
            >
              {t('dataroom.firstLabel.status.changes')}
            </button>
          )}
        </span>
        {revision && !canUpdate && (
          <ReportUpdateSummary isOpen={isSummaryOpen} onClose={() => setSummaryOpen(false)} revision={revision} />
        )}
        {canUpdate && (
          <ConfirmModal
            isOpen={isUpdateConfirmOpen}
            onConfirm={confirmUpdate}
            onCancel={() => setUpdateConfirmOpen(false)}
            title={t('dataroom.firstLabel.status.updateTitle')}
            message={t('dataroom.firstLabel.status.updateMessage', { count: pendingDocuments })}
            confirmLabel={
              isStartingUpdate ? t('dataroom.firstLabel.status.starting') : t('dataroom.firstLabel.status.update')
            }
            variant="warning"
          />
        )}
      </>
    );
  }
  if (run.status === 'failed') {
    // A failed run with exported deliverables no longer gates the workspace
    // (see useFirstLabelRun), so the retry affordance must live here.
    return (
      <>
        <span
          className="flex items-center gap-1 text-xs text-red-500 whitespace-nowrap"
          title={
            hasDeliverables
              ? t('dataroom.firstLabel.status.failedWithDeliverables')
              : t('dataroom.firstLabel.status.failedNeedsReview')
          }
          data-testid="first-label-failed"
        >
          <Icon icon="mdi:alert-circle-outline" className="size-3.5" />
          {hasDeliverables && !readOnly && (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={isStarting}
              data-testid="first-label-status-retry"
              className="cursor-pointer underline decoration-dotted hover:text-red-600 disabled:opacity-50"
            >
              {isStarting ? t('dataroom.firstLabel.status.retrying') : t('dataroom.firstLabel.status.retry')}
            </button>
          )}
        </span>
        {hasDeliverables && !readOnly && rerunModal}
      </>
    );
  }
  return null;
}
